// Native (iOS APNs / Android FCM) push, the mobile-app counterpart to
// pushClient.ts (which is web/desktop VAPID web-push). Everything here is a
// clean no-op on web/desktop — every entry point guards on
// Capacitor.isNativePlatform() — and the plugin is dynamically imported only on
// native, mirroring the codebase idiom (LoginPage's biometric import,
// openExternal, appleSignIn).
//
// On/off model (calibrated to current scale — per-device, no extra server
// plumbing): the SERVER token IS the delivery switch. Enabling registers the
// device token; disabling drops it AND sets a per-device opt-out flag so a
// later login on this device doesn't silently re-enable. Logout drops the token
// (shared-device privacy) WITHOUT setting opt-out, so the owner re-registers on
// next login.

import { Capacitor } from "@capacitor/core";
// Static import ON PURPOSE: webpack dynamic-import chunks silently
// never load inside the Capacitor WKWebView (the settings toggle froze at
// "loading plugin" forever). Capacitor plugin shims are tiny and safe in the
// web bundle — every call site still guards on isNativePlatform().
import { PushNotifications } from "@capacitor/push-notifications";
import api from "../api";
import { t } from "../i18n";

export const NATIVE_PUSH_SUPPORTED = Capacitor.isNativePlatform();

const OPT_OUT_KEY = "rq_native_push_optout";

type PermStatus = "granted" | "denied" | "prompt" | "unsupported";

let listenersAttached = false;
let lastToken: string | null = null;

// A hung plugin/bridge call must never strand the UI in a busy state (seen on
// device: settings toggle froze with no OS prompt). Short window for
// programmatic calls; requestPermissions gets a long one because it (rightly)
// waits on a human reading the OS dialog.
const BRIDGE_TIMEOUT_MS = 15_000;
const PROMPT_TIMEOUT_MS = 90_000;
const withTimeout = <T,>(p: Promise<T>, ms: number, what: string): Promise<T> =>
  Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(t("nativePush.nativePushTimedOut", { what, ms }))), ms)
    ),
  ]);

// ⚠️ NEVER pass the plugin object itself through an `await` (or `return` it
// from an async function). Capacitor plugin objects are Proxies that fabricate
// a method for ANY property — including `then` — so promise resolution calls
// that phantom `then` on the native bridge and hangs forever. That bug shipped
// in Stage 1 as `const loadPlugin = async () => PushNotifications` and froze
// the settings toggle. Call methods on the imported
// object directly.

const normalizePerm = (receive: string): PermStatus => {
  if (receive === "granted") return "granted";
  if (receive === "denied") return "denied";
  return "prompt"; // 'prompt' | 'prompt-with-rationale'
};

export const isNativePushOptedOut = (): boolean => {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

export const getNativePushPermission = async (): Promise<PermStatus> => {
  if (!NATIVE_PUSH_SUPPORTED) return "unsupported";
  try {
    const { receive } = await PushNotifications.checkPermissions();
    return normalizePerm(receive);
  } catch {
    return "unsupported";
  }
};

const sendTokenToServer = async (token: string) => {
  lastToken = token;
  try {
    await api.post("/push/register-native", {
      token,
      platform: Capacitor.getPlatform(),
    });
  } catch (err) {
    console.error("[native-push] failed to register token with server", err);
  }
};

// Android files every push under a channel, and the channel decides how loud
// it is. With none of our own, FCM drops our alerts in its "Miscellaneous"
// fallback at default importance: no heads-up banner, so a new request can
// sit unseen in a loud room. This one is HIGH, and the manifest names it as
// the FCM default, so every server push lands here without a channel id.
// Must match default_notification_channel_id in AndroidManifest.xml.
const ANDROID_CHANNEL_ID = "requests";

const ensureAndroidChannel = async () => {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await withTimeout(
      PushNotifications.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: t("nativePush.androidChannelName"),
        importance: 5,
        visibility: 1,
        vibration: true,
      }),
      BRIDGE_TIMEOUT_MS,
      "createChannel"
    );
  } catch (err) {
    // Not fatal: pushes still arrive, just in the quieter fallback channel.
    console.error("[native-push] could not create the Android channel", err);
  }
};

const attachListeners = async () => {
  if (listenersAttached) return;
  listenersAttached = true;

  await ensureAndroidChannel();

  // The APNs (iOS) / FCM (Android) token — the plugin abstracts both behind one
  // event. Ship it to the server keyed to the logged-in user (api carries the
  // Bearer token + CSRF automatically).
  PushNotifications.addListener("registration", (token: { value: string }) => {
    sendTokenToServer(token.value);
  });

  PushNotifications.addListener("registrationError", (err: unknown) => {
    console.error("[native-push] registration error", err);
  });

  // Tap on a notification → open the right request, matching public/sw.js's
  // notificationclick deep-link (/requests?focus=orderId).
  PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action: { notification?: { data?: { orderId?: string; url?: string } } }) => {
      const data = (action && action.notification && action.notification.data) || {};
      const base = data.url || "/requests";
      const target = data.orderId ? `${base}?focus=${data.orderId}` : base;
      window.location.assign(target);
    }
  );

  // Foreground receipt: the in-app RequestsPage already live-refreshes, so we
  // intentionally don't pop a duplicate in-app banner here.
  PushNotifications.addListener("pushNotificationReceived", () => {});
};

const dropServerToken = async () => {
  try {
    await PushNotifications.removeAllListeners();
    listenersAttached = false;
  } catch {
    /* ignore */
  }
  if (lastToken) {
    try {
      await api.post("/push/unregister-native", { token: lastToken });
    } catch (err) {
      console.error("[native-push] failed to unregister token", err);
    }
  }
};

/**
 * Sign-in path (founder decision — supersedes the
 * old "never auto-prompt at login" rule): push should be ON by default, so
 * the OS permission prompt fires the moment a talent is signed in on native.
 *
 * Honors the explicit choices we must never override:
 *   - per-device opt-out flag (they toggled push OFF in settings) → skip;
 *   - permission already denied on a PREVIOUS occasion → skip quietly
 *     (no re-nag on every app open; the settings toggle is their way back).
 *
 * Returns what the caller needs for UI:
 *   "granted"       — registered (fresh grant or already granted);
 *   "denied_prompt" — they hit "Don't Allow" JUST NOW → show the
 *                     "Notifications are off" popup;
 *   "skipped"       — opted out / previously denied / not native / error.
 */
export const ensureNativePushOnSignIn = async (
  // Awaited ONLY when the OS prompt is about to appear (permission still
  // "prompt"): the caller holds it until the first-sign-in moment has settled
  // (lib/firstSignInMoment). An already-granted install registers right away.
  // A rejection = don't prompt (signed out meanwhile) → "skipped".
  beforePrompt?: () => Promise<void>
): Promise<"granted" | "denied_prompt" | "skipped"> => {
  if (!NATIVE_PUSH_SUPPORTED || isNativePushOptedOut()) return "skipped";
  try {
    let { receive } = await withTimeout(
      PushNotifications.checkPermissions(),
      BRIDGE_TIMEOUT_MS,
      "checkPermissions"
    );
    let status = normalizePerm(receive);
    if (status === "denied") return "skipped"; // denied in the past — don't nag
    let promptedNow = false;
    if (status === "prompt") {
      if (beforePrompt) await beforePrompt();
      promptedNow = true;
      ({ receive } = await withTimeout(
        PushNotifications.requestPermissions(),
        PROMPT_TIMEOUT_MS,
        "requestPermissions"
      ));
      status = normalizePerm(receive);
    }
    if (status !== "granted") {
      return promptedNow ? "denied_prompt" : "skipped";
    }
    await attachListeners();
    await withTimeout(PushNotifications.register(), BRIDGE_TIMEOUT_MS, "register");
    return "granted";
  } catch (err) {
    console.error("[native-push] sign-in onboarding failed", err);
    return "skipped";
  }
};

/** Settings toggle ON: prompt if needed, then register. Returns the status.
 *  Throws on plugin/bridge failure (incl. timeouts) so the UI can show a
 *  retry message instead of the misleading "denied in Settings" guidance. */
export const enableNativePush = async (): Promise<PermStatus> => {
  if (!NATIVE_PUSH_SUPPORTED) return "unsupported";
  console.log("[native-push] enable: loading plugin");
  try {
    localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* ignore */
  }
  let { receive } = await withTimeout(
    PushNotifications.checkPermissions(),
    BRIDGE_TIMEOUT_MS,
    "checkPermissions"
  );
  console.log("[native-push] enable: permission =", receive);
  if (normalizePerm(receive) === "prompt") {
    ({ receive } = await withTimeout(
      PushNotifications.requestPermissions(),
      PROMPT_TIMEOUT_MS,
      "requestPermissions"
    ));
    console.log("[native-push] enable: prompt result =", receive);
  }
  const status = normalizePerm(receive);
  if (status !== "granted") return status;
  await attachListeners();
  await withTimeout(PushNotifications.register(), BRIDGE_TIMEOUT_MS, "register");
  console.log("[native-push] enable: registered, awaiting token event");
  return "granted";
};

/** Settings toggle OFF: opt out on this device + drop the server token. */
export const disableNativePush = async (): Promise<void> => {
  if (!NATIVE_PUSH_SUPPORTED) return;
  try {
    localStorage.setItem(OPT_OUT_KEY, "1");
  } catch {
    /* ignore */
  }
  await dropServerToken();
};

/**
 * Logout cleanup. Drop THIS device's token so the next user on a shared phone
 * can't receive the previous user's request alerts. Does NOT set the opt-out
 * flag, so logging back in re-registers normally.
 */
export const unregisterNativePushForLogout = async (): Promise<void> => {
  if (!NATIVE_PUSH_SUPPORTED) return;
  await dropServerToken();
};
